/**
 * Utility hooks and functions for draggable UI panels
 */

import { useRef, useEffect, useState, useCallback } from 'react';

interface DragState {
  isDragging: boolean;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
}

interface DraggablePanelOptions {
  storageKey?: string;
  clampToViewport?: boolean;
}

const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

const getStoredPosition = (storageKey: string): { x: number; y: number } | null => {
  try {
    const raw: string | null = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const x = (parsed as { x?: unknown }).x;
    const y = (parsed as { y?: unknown }).y;
    if (typeof x !== 'number' || typeof y !== 'number') return null;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  } catch {
    return null;
  }
};

const saveStoredPosition = (storageKey: string, pos: { x: number; y: number }): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(pos));
  } catch {
    // ignore
  }
};

/**
 * Hook to make a panel draggable by its header
 * Returns position state and event handlers for the header element
 */
export const useDraggablePanel = (
  initialX: number = 0,
  initialY: number = 0,
  options?: DraggablePanelOptions
) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    if (options?.storageKey) {
      const stored = getStoredPosition(options.storageKey);
      if (stored) return stored;
    }
    return { x: initialX, y: initialY };
  });
  const dragStateRef = useRef<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });

  const clampPositionIfNeeded = useCallback((next: { x: number; y: number }): { x: number; y: number } => {
    if (!options?.clampToViewport) return next;
    const el = panelRef.current;
    if (!el) return next;

    const rect = el.getBoundingClientRect();
    const panelWidth = rect.width;

    const maxX = Math.max(0, window.innerWidth - panelWidth);

    return {
      x: clamp(next.x, 0, maxX),
      y: next.y,
    };
  }, [options?.clampToViewport]);

  const handleHeaderMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    dragStateRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: position.x,
      offsetY: position.y,
    };
  }, [position]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStateRef.current.isDragging) return;

      const deltaX = e.clientX - dragStateRef.current.startX;
      const deltaY = e.clientY - dragStateRef.current.startY;

      const next = clampPositionIfNeeded({
        x: dragStateRef.current.offsetX + deltaX,
        y: dragStateRef.current.offsetY + deltaY,
      });
      setPosition(next);
    };

    const handleMouseUp = () => {
      if (dragStateRef.current.isDragging) {
        dragStateRef.current.isDragging = false;
        if (options?.storageKey) {
          saveStoredPosition(options.storageKey, position);
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [clampPositionIfNeeded, options?.storageKey, position]);

  useEffect(() => {
    if (!options?.clampToViewport) return;
    const handleResize = (): void => {
      setPosition((prev) => clampPositionIfNeeded(prev));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampPositionIfNeeded, options?.clampToViewport]);

  return {
    position,
    panelRef,
    handleHeaderMouseDown,
    style: {
      transform: `translate(${position.x}px, ${position.y}px)`,
      transition: dragStateRef.current.isDragging ? 'none' : 'transform 0.2s ease-out',
    },
  };
};

/**
 * CSS class for draggable headers
 */
export const draggableHeaderStyle = {
  cursor: 'grab',
  userSelect: 'none' as const,
  transition: 'background-color 0.2s ease',
};

export const draggableHeaderHoverStyle = {
  ...draggableHeaderStyle,
  cursor: 'grabbing',
};
